import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/pos/admin/common/multi-select";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import type { Discount, DiscountScope } from "@/types/pos";
import { ArrowLeft, Loader2 } from "lucide-react";

// --- Static option sets ------------------------------------------------

const SCOPE_OPTIONS: { value: DiscountScope; label: string }[] = [
  { value: "order", label: "Orders" },
  { value: "membership", label: "Memberships" },
  { value: "entry_pass", label: "Entry Passes" },
];

const GUEST_TYPE_OPTIONS = [
  { value: "couples", label: "Couples" },
  { value: "single_ladies", label: "Single Ladies" },
  { value: "single_males", label: "Single Males" },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const TIME_SLOT_OPTIONS = [
  { value: "daytime", label: "Daytime" },
  { value: "evening", label: "Evening" },
];

// --- Form state shape --------------------------------------------------

interface DiscountFormState {
  name: string;
  description: string;
  discountType: "flat" | "percentage";
  discountValue: string;
  maxDiscountAmount: string;
  isAutoApply: boolean;
  promoCode: string;
  active: boolean;
  // Scope
  appliesTo: DiscountScope[];
  // Membership targeting
  includedMembershipTemplates: number[];
  includedGuestTypes: string[];
  // Entry pass targeting
  includedEntryPassTemplates: number[];
  includedDaysOfWeek: number[];
  includedTimeSlots: string[];
  // Order targeting
  includedMenuTypes: number[];
  includedLocations: number[];
  includedCategories: string[];
  includedSubcategories: number[];
  includedProducts: number[];
  excludedMenuTypes: number[];
  excludedLocations: number[];
  excludedCategories: string[];
  excludedSubcategories: number[];
  excludedProducts: number[];
  // Payment method targeting
  includedPaymentMethods: string[];
  // Constraints
  minimumSubtotal: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  globalUsageLimit: string;
  perCustomerUsageLimit: string;
}

const EMPTY_FORM: DiscountFormState = {
  name: "",
  description: "",
  discountType: "percentage",
  discountValue: "",
  maxDiscountAmount: "",
  isAutoApply: false,
  promoCode: "",
  active: true,
  appliesTo: ["order"],
  includedMembershipTemplates: [],
  includedGuestTypes: [],
  includedEntryPassTemplates: [],
  includedDaysOfWeek: [],
  includedTimeSlots: [],
  includedMenuTypes: [],
  includedLocations: [],
  includedCategories: [],
  includedSubcategories: [],
  includedProducts: [],
  excludedMenuTypes: [],
  excludedLocations: [],
  excludedCategories: [],
  excludedSubcategories: [],
  excludedProducts: [],
  includedPaymentMethods: [],
  minimumSubtotal: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  globalUsageLimit: "",
  perCustomerUsageLimit: "",
};

// --- Helpers -----------------------------------------------------------

function toNullableArray<T>(arr: T[]): T[] | null {
  return arr.length > 0 ? arr : null;
}

function fromNullableArray<T>(arr: T[] | null | undefined): T[] {
  return arr ?? [];
}

// --- Component ---------------------------------------------------------

interface DiscountEditorProps {
  discountId?: string;
  onBack: () => void;
}

export default function DiscountEditor({ discountId, onBack }: DiscountEditorProps) {
  const isEdit = !!discountId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<DiscountFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // --- Fetch existing discount for edit mode ---------------------------

  const { data: existingDiscount, isLoading: isLoadingDiscount } = useQuery<Discount>({
    queryKey: ["/v1/pos/discounts", discountId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/discounts/${discountId}`);
      if (!res.ok) throw new Error("Failed to fetch discount");
      return res.json();
    },
    enabled: isEdit,
  });

  // Populate form when discount loads
  useEffect(() => {
    if (!existingDiscount) return;
    const d = existingDiscount;
    setForm({
      name: d.name ?? "",
      description: d.description ?? "",
      discountType: d.discountType,
      discountValue: d.discountValue ?? "",
      maxDiscountAmount: d.maxDiscountAmount ?? "",
      isAutoApply: d.isAutoApply,
      promoCode: d.promoCode ?? "",
      active: d.active,
      appliesTo: d.appliesTo?.length ? d.appliesTo : ["order"],
      includedMembershipTemplates: fromNullableArray(d.includedMembershipTemplates),
      includedGuestTypes: fromNullableArray(d.includedGuestTypes),
      includedEntryPassTemplates: fromNullableArray(d.includedEntryPassTemplates),
      includedDaysOfWeek: fromNullableArray(d.includedDaysOfWeek),
      includedTimeSlots: fromNullableArray(d.includedTimeSlots),
      includedMenuTypes: fromNullableArray(d.includedMenuTypes),
      includedLocations: fromNullableArray(d.includedLocations),
      includedCategories: fromNullableArray(d.includedCategories),
      includedSubcategories: fromNullableArray(d.includedSubcategories),
      includedProducts: fromNullableArray(d.includedProducts),
      excludedMenuTypes: fromNullableArray(d.excludedMenuTypes),
      excludedLocations: fromNullableArray(d.excludedLocations),
      excludedCategories: fromNullableArray(d.excludedCategories),
      excludedSubcategories: fromNullableArray(d.excludedSubcategories),
      excludedProducts: fromNullableArray(d.excludedProducts),
      includedPaymentMethods: fromNullableArray(d.includedPaymentMethods),
      minimumSubtotal: d.minimumSubtotal ?? "",
      startDate: d.startDate ? d.startDate.slice(0, 10) : "",
      endDate: d.endDate ? d.endDate.slice(0, 10) : "",
      startTime: d.startTime ?? "",
      endTime: d.endTime ?? "",
      globalUsageLimit: d.globalUsageLimit != null ? String(d.globalUsageLimit) : "",
      perCustomerUsageLimit: d.perCustomerUsageLimit != null ? String(d.perCustomerUsageLimit) : "",
    });
  }, [existingDiscount]);

  // --- Fetch reference data for dropdowns ------------------------------

  const { data: locations = [], isLoading: loadingLocations } = useQuery({
    queryKey: ["/v1/pos/locations"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/locations");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: menuTypes = [], isLoading: loadingMenuTypes } = useQuery({
    queryKey: ["/v1/pos/menu-types"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/menu-types");
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data?.data ?? [];
    },
  });

  const { data: products = [], isLoading: loadingProducts } = useQuery({
    queryKey: ["/v1/pos/products"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/products");
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data?.data ?? [];
    },
  });

  const { data: subcategories = [], isLoading: loadingSubcategories } = useQuery({
    queryKey: ["/v1/pos/subcategories"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/subcategories");
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data?.data ?? [];
    },
  });

  const { data: membershipTemplatesRaw, isLoading: loadingMembershipTemplates } = useQuery({
    queryKey: ["/v1/memberships/templates"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/memberships/templates");
      if (!res.ok) return [];
      const data = await res.json();
      return data?.data ?? data ?? [];
    },
  });
  const membershipTemplates = useMemo(() => Array.isArray(membershipTemplatesRaw) ? membershipTemplatesRaw : [], [membershipTemplatesRaw]);

  const { data: entryPassTemplatesRaw, isLoading: loadingEntryPassTemplates } = useQuery({
    queryKey: ["/v1/entry-passes/templates"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/entry-passes/templates");
      if (!res.ok) return [];
      const data = await res.json();
      return data?.data ?? data ?? [];
    },
  });
  const entryPassTemplates = useMemo(() => Array.isArray(entryPassTemplatesRaw) ? entryPassTemplatesRaw : [], [entryPassTemplatesRaw]);

  // --- Derived dropdown options ----------------------------------------

  const locationOptions = useMemo(
    () => locations.map((l: any) => ({ value: l.id, label: l.name })),
    [locations]
  );

  const menuTypeOptions = useMemo(
    () => menuTypes.map((m: any) => ({ value: m.id, label: m.name })),
    [menuTypes]
  );

  const productOptions = useMemo(
    () => products.map((p: any) => ({ value: p.id, label: p.name })),
    [products]
  );

  const subcategoryOptions = useMemo(
    () => subcategories.map((s: any) => ({ value: s.id, label: s.displayName || s.name })),
    [subcategories]
  );

  const categoryOptions = useMemo(() => {
    const cats = new Set<string>();
    subcategories.forEach((s: any) => { if (s.category) cats.add(s.category); });
    products.forEach((p: any) => { if (p.category) cats.add(p.category); });
    return Array.from(cats).sort().map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));
  }, [subcategories, products]);

  const membershipTemplateOptions = useMemo(
    () => membershipTemplates.map((t: any) => ({
      value: t.id,
      label: `${t.name} (${t.durationMonths}mo)`,
    })),
    [membershipTemplates]
  );

  const entryPassTemplateOptions = useMemo(
    () => entryPassTemplates.map((t: any) => ({
      value: t.id,
      label: `${t.name}`,
    })),
    [entryPassTemplates]
  );

  // --- Scope helpers ---------------------------------------------------

  const hasScope = (scope: DiscountScope) => form.appliesTo.includes(scope);

  const toggleScope = (scope: DiscountScope) => {
    setForm((prev) => {
      const next = prev.appliesTo.includes(scope)
        ? prev.appliesTo.filter((s) => s !== scope)
        : [...prev.appliesTo, scope];
      // Never allow empty
      if (next.length === 0) return prev;
      return { ...prev, appliesTo: next };
    });
  };

  // --- Field updaters --------------------------------------------------

  const set = <K extends keyof DiscountFormState>(key: K, value: DiscountFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // --- Submit ----------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!form.name.trim()) {
      toast({ title: "Validation Error", description: "Discount name is required", variant: "destructive" });
      return;
    }
    if (!form.discountValue || parseFloat(form.discountValue) <= 0) {
      toast({ title: "Validation Error", description: "Discount value must be greater than 0", variant: "destructive" });
      return;
    }
    if (form.discountType === "percentage" && parseFloat(form.discountValue) > 100) {
      toast({ title: "Validation Error", description: "Percentage discount cannot exceed 100%", variant: "destructive" });
      return;
    }
    if (!form.isAutoApply && !form.promoCode.trim()) {
      toast({ title: "Validation Error", description: "Either enable auto-apply or provide a promo code", variant: "destructive" });
      return;
    }
    if (form.appliesTo.length === 0) {
      toast({ title: "Validation Error", description: "Select at least one scope (Orders, Memberships, or Entry Passes)", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const body: Record<string, any> = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        discountType: form.discountType,
        discountValue: form.discountValue,
        maxDiscountAmount: form.maxDiscountAmount || null,
        isAutoApply: form.isAutoApply,
        promoCode: form.isAutoApply ? null : form.promoCode.trim() || null,
        active: form.active,
        appliesTo: form.appliesTo,
        // Membership targeting
        includedMembershipTemplates: toNullableArray(form.includedMembershipTemplates),
        includedGuestTypes: toNullableArray(form.includedGuestTypes),
        // Entry pass targeting
        includedEntryPassTemplates: toNullableArray(form.includedEntryPassTemplates),
        includedDaysOfWeek: toNullableArray(form.includedDaysOfWeek),
        includedTimeSlots: toNullableArray(form.includedTimeSlots),
        // Order targeting
        includedMenuTypes: toNullableArray(form.includedMenuTypes),
        includedLocations: toNullableArray(form.includedLocations),
        includedCategories: toNullableArray(form.includedCategories),
        includedSubcategories: toNullableArray(form.includedSubcategories),
        includedProducts: toNullableArray(form.includedProducts),
        excludedMenuTypes: toNullableArray(form.excludedMenuTypes),
        excludedLocations: toNullableArray(form.excludedLocations),
        excludedCategories: toNullableArray(form.excludedCategories),
        excludedSubcategories: toNullableArray(form.excludedSubcategories),
        excludedProducts: toNullableArray(form.excludedProducts),
        // Payment method targeting
        includedPaymentMethods: toNullableArray(form.includedPaymentMethods),
        // Constraints
        minimumSubtotal: form.minimumSubtotal || null,
        startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
        endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        globalUsageLimit: form.globalUsageLimit ? Number(form.globalUsageLimit) : null,
        perCustomerUsageLimit: form.perCustomerUsageLimit ? Number(form.perCustomerUsageLimit) : null,
      };

      const url = isEdit ? `/v1/pos/discounts/${discountId}` : "/v1/pos/discounts";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, {
        method,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} discount`);
      }

      toast({ title: `Discount ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/discounts"] });
      onBack();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || `Failed to ${isEdit ? "update" : "create"} discount`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Loading state ---------------------------------------------------

  if (isEdit && isLoadingDiscount) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Discounts
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  // --- Render ----------------------------------------------------------

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Discounts
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit Discount" : "Create Discount"}
        </h2>
        <div className="w-[140px]" /> {/* spacer */}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        {/* ── Basic Info ─────────────────────────────────────────── */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Name *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Summer Special 20% Off"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Description</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Optional description"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Discount Type *</Label>
                <Select value={form.discountType} onValueChange={(v) => set("discountType", v as "flat" | "percentage")}>
                  <SelectTrigger className="h-[36px] text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">
                  Value * {form.discountType === "percentage" ? "(%)" : "($)"}
                </Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  step="0.01"
                  min="0"
                  max={form.discountType === "percentage" ? "100" : undefined}
                  value={form.discountValue}
                  onChange={(e) => set("discountValue", e.target.value)}
                  placeholder={form.discountType === "percentage" ? "e.g. 15" : "e.g. 5.00"}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Max Discount ($)</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.maxDiscountAmount}
                  onChange={(e) => set("maxDiscountAmount", e.target.value)}
                  placeholder="No cap"
                />
              </div>
            </div>

            {/* Activation */}
            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.isAutoApply}
                    onCheckedChange={(v) => set("isAutoApply", v)}
                  />
                  <Label className="text-[12px]">Auto-Apply</Label>
                </div>
                <p className="text-[11px] text-[var(--secondary-text-color)]">
                  {form.isAutoApply
                    ? "Discount applies automatically when conditions are met"
                    : "Customer must enter a promo code"}
                </p>
              </div>
              {!form.isAutoApply && (
                <div className="space-y-1">
                  <Label className="text-[12px]">Promo Code *</Label>
                  <Input
                    className="h-[36px] text-[13px] uppercase"
                    value={form.promoCode}
                    onChange={(e) => set("promoCode", e.target.value.toUpperCase())}
                    placeholder="e.g. SUMMER2024"
                  />
                </div>
              )}
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
              <Label className="text-[12px]">Active</Label>
            </div>
          </CardContent>
        </Card>

        {/* ── Scope (Applies To) ──────────────────────────────── */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Applies To</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-[11px] text-[var(--secondary-text-color)] mb-2">
              Select which transaction types this discount can be used for. At least one is required.
            </p>
            <div className="flex gap-4">
              {SCOPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={hasScope(opt.value)}
                    onCheckedChange={() => toggleScope(opt.value)}
                  />
                  <span className="text-[13px]">{opt.label}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Membership Targeting ─────────────────────────────── */}
        {hasScope("membership") && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-[14px] font-semibold">Membership Targeting</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <p className="text-[11px] text-[var(--secondary-text-color)]">
                Leave empty to apply to all. Select specific items to restrict.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-[12px]">Membership Templates</Label>
                  <MultiSelect
                    options={membershipTemplateOptions}
                    selected={form.includedMembershipTemplates}
                    onChange={(vals) => set("includedMembershipTemplates", vals as number[])}
                    placeholder="All templates"
                    loading={loadingMembershipTemplates}
                    isRequired={false}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[12px]">Guest Types</Label>
                  <MultiSelect
                    options={GUEST_TYPE_OPTIONS}
                    selected={form.includedGuestTypes}
                    onChange={(vals) => set("includedGuestTypes", vals as string[])}
                    placeholder="All guest types"
                    isRequired={false}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Entry Pass Targeting ─────────────────────────────── */}
        {hasScope("entry_pass") && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-[14px] font-semibold">Entry Pass Targeting</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <p className="text-[11px] text-[var(--secondary-text-color)]">
                Leave empty to apply to all. Select specific items to restrict.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-[12px]">Entry Pass Templates</Label>
                  <MultiSelect
                    options={entryPassTemplateOptions}
                    selected={form.includedEntryPassTemplates}
                    onChange={(vals) => set("includedEntryPassTemplates", vals as number[])}
                    placeholder="All templates"
                    loading={loadingEntryPassTemplates}
                    isRequired={false}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[12px]">Days of Week</Label>
                  <MultiSelect
                    options={DAY_OF_WEEK_OPTIONS}
                    selected={form.includedDaysOfWeek}
                    onChange={(vals) => set("includedDaysOfWeek", vals as number[])}
                    placeholder="All days"
                    isRequired={false}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[12px]">Time Slots</Label>
                  <MultiSelect
                    options={TIME_SLOT_OPTIONS}
                    selected={form.includedTimeSlots}
                    onChange={(vals) => set("includedTimeSlots", vals as string[])}
                    placeholder="All time slots"
                    isRequired={false}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Order Targeting ──────────────────────────────────── */}
        {hasScope("order") && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-[14px] font-semibold">Order Targeting</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <p className="text-[11px] text-[var(--secondary-text-color)]">
                Leave empty to apply to all. Use include to whitelist, exclude to blacklist.
              </p>

              {/* Include section */}
              <div className="space-y-2">
                <p className="text-[12px] font-medium text-green-700">Include</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[12px]">Locations</Label>
                    <MultiSelect
                      options={locationOptions}
                      selected={form.includedLocations}
                      onChange={(vals) => set("includedLocations", vals as number[])}
                      placeholder="All locations"
                      loading={loadingLocations}
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px]">Menu Types</Label>
                    <MultiSelect
                      options={menuTypeOptions}
                      selected={form.includedMenuTypes}
                      onChange={(vals) => set("includedMenuTypes", vals as number[])}
                      placeholder="All menu types"
                      loading={loadingMenuTypes}
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px]">Categories</Label>
                    <MultiSelect
                      options={categoryOptions}
                      selected={form.includedCategories}
                      onChange={(vals) => set("includedCategories", vals as string[])}
                      placeholder="All categories"
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px]">Subcategories</Label>
                    <MultiSelect
                      options={subcategoryOptions}
                      selected={form.includedSubcategories}
                      onChange={(vals) => set("includedSubcategories", vals as number[])}
                      placeholder="All subcategories"
                      loading={loadingSubcategories}
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-[12px]">Products</Label>
                    <MultiSelect
                      options={productOptions}
                      selected={form.includedProducts}
                      onChange={(vals) => set("includedProducts", vals as number[])}
                      placeholder="All products"
                      loading={loadingProducts}
                      isRequired={false}
                    />
                  </div>
                </div>
              </div>

              {/* Exclude section */}
              <div className="space-y-2 pt-2 border-t border-[var(--layout-border-color)]">
                <p className="text-[12px] font-medium text-red-700">Exclude</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[12px]">Locations</Label>
                    <MultiSelect
                      options={locationOptions}
                      selected={form.excludedLocations}
                      onChange={(vals) => set("excludedLocations", vals as number[])}
                      placeholder="None excluded"
                      loading={loadingLocations}
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px]">Menu Types</Label>
                    <MultiSelect
                      options={menuTypeOptions}
                      selected={form.excludedMenuTypes}
                      onChange={(vals) => set("excludedMenuTypes", vals as number[])}
                      placeholder="None excluded"
                      loading={loadingMenuTypes}
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px]">Categories</Label>
                    <MultiSelect
                      options={categoryOptions}
                      selected={form.excludedCategories}
                      onChange={(vals) => set("excludedCategories", vals as string[])}
                      placeholder="None excluded"
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px]">Subcategories</Label>
                    <MultiSelect
                      options={subcategoryOptions}
                      selected={form.excludedSubcategories}
                      onChange={(vals) => set("excludedSubcategories", vals as number[])}
                      placeholder="None excluded"
                      loading={loadingSubcategories}
                      isRequired={false}
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-[12px]">Products</Label>
                    <MultiSelect
                      options={productOptions}
                      selected={form.excludedProducts}
                      onChange={(vals) => set("excludedProducts", vals as number[])}
                      placeholder="None excluded"
                      loading={loadingProducts}
                      isRequired={false}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Payment Method Targeting ────────────────────────── */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Payment Method Targeting</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-[11px] text-[var(--secondary-text-color)] mb-2">
              Restrict this discount to specific payment methods. Leave empty to allow all methods.
            </p>
            <div className="flex gap-4">
              {[
                { value: "TOKEN", label: "SECH Token" },
                { value: "CARD", label: "Credit Card" },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.includedPaymentMethods.includes(opt.value)}
                    onCheckedChange={() => {
                      const current = form.includedPaymentMethods;
                      set(
                        "includedPaymentMethods",
                        current.includes(opt.value)
                          ? current.filter((v) => v !== opt.value)
                          : [...current, opt.value]
                      );
                    }}
                  />
                  <span className="text-[13px]">{opt.label}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Constraints ──────────────────────────────────────── */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Constraints</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Minimum Subtotal ($)</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.minimumSubtotal}
                  onChange={(e) => set("minimumSubtotal", e.target.value)}
                  placeholder="No minimum"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Global Usage Limit</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  min="0"
                  step="1"
                  value={form.globalUsageLimit}
                  onChange={(e) => set("globalUsageLimit", e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Per Customer Limit</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  min="0"
                  step="1"
                  value={form.perCustomerUsageLimit}
                  onChange={(e) => set("perCustomerUsageLimit", e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Start Date</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => set("startDate", e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">End Date</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => set("endDate", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Start Time (daily window)</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => set("startTime", e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">End Time (daily window)</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => set("endTime", e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Actions ──────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-[36px] text-[13px]"
            onClick={onBack}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]"
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Discount" : "Create Discount"}
          </Button>
        </div>
      </form>
    </div>
  );
}
